import { useState, useEffect, useMemo } from "react";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import useGetResource from "@/hooks/useGetResources";
import AddUser from "./AddUser";
import EditUser from "./EditUser";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import ViewUserProfile from "@/pages/user/profile/viewUser";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  Paper,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { FaUserPlus } from "react-icons/fa";

// Import sub-components
import PageHeader from "@/components/PageHeader";
import EmptyUsersState from "./components/EmptyUsersState";
import UsersTableHeader from "./components/UsersTableHeader";
import UserTableRow from "./components/UserTableRow";
import NewDeleteModal from "@/components/newDeleteModal";

const MyUserManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  // === Pagination States ===
  const [pages, setPages] = useState([]);
  const [pageTokens, setPageTokens] = useState([null]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [viewProfile, setViewProfile] = useState(false);
  const [viewedUser, setViewedUser] = useState();

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Derive queryUrl based on the current page token
  const currentPageToken = pageTokens[pageTokens.length - 1];

  const handleUserDetails = (user) => {
    setViewedUser(user);
    setViewProfile(true);
  };

  // Build endpoint with cursor param
  const queryUrl = currentPageToken
    ? `/api/v1/users?cursor=${currentPageToken}`
    : `/api/v1/users`;

  // Use the generic hook here
  const {
    data: myusers,
    isLoading: myusersloading,
    error: errorMyUsers,
    refetch: refetchUsers,
  } = useGetResource(["users", currentPageToken], queryUrl);

  useEffect(() => {
    if (!myusers?.records) return;

    const newPageIndex = pageTokens.length - 1;

    // For first page (page 0), we should reset pages array
    if (newPageIndex === 0) {
      setPages([myusers.records]);
      setCurrentPageIndex(0);
    } else {
      // For subsequent pages, add to pages array
      setPages((prevPages) => {
        const newPages = [...prevPages];
        newPages[newPageIndex] = myusers.records;
        return newPages;
      });
      setCurrentPageIndex(newPageIndex);
    }
  }, [myusers, pageTokens]);

  const aggregatedUsers = useMemo(
    () => pages.slice(0, currentPageIndex + 1).flat(),
    [pages, currentPageIndex]
  );

  const [expandedUserId, setExpandedUserId] = useState(null);
  const { userDetails } = useUsersSettingService(expandedUserId);

  const [editingUser, setEditingUser] = useState(null);
  const { userDetails: editedUserDetails } =
    useUsersSettingService(editingUser);

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openAddForm, setOpenAddForm] = useState(false);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const { mutateAsync: deleteUser } = useDeleteData("/api/v1/org-admin/users", [
    "users",
  ]);

  const handleAccordion = (userId, event) => {
    event.stopPropagation();
    setExpandedUserId((prevUserId) => (prevUserId === userId ? null : userId));
  };

  const handleOpenEditModal = (userId, event) => {
    event.stopPropagation();
    setEditingUser(userId);
    setOpenEditForm(true);
  };

  const handleDeleteClick = (userId, event) => {
    event.stopPropagation();
    setItemToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteUser(itemToDelete);
      // Reset pagination to ensure fresh data
      setPages([]);
      setPageTokens([null]);
      setCurrentPageIndex(0);
      await refetchUsers();
      setToast({
        open: true,
        message: t("myUserManagement.deleteSuccessToast"),
        severity: "success",
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setItemToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleNextPage = () => {
    if (myusers?.next_page) {
      setPageTokens((prev) => [...prev, myusers.next_page]);
    }
  };

  const hasNextPage = myusers && myusers.next_page !== null;

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortUsers = (users) => {
    if (!sortConfig.key || !users) return users;

    const sorted = [...users];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "first_name":
          aValue = a.first_name || "";
          bValue = b.first_name || "";
          break;
        case "last_name":
          aValue = a.last_name || "";
          bValue = b.last_name || "";
          break;
        case "username":
          aValue = a.username || "";
          bValue = b.username || "";
          break;
        case "created_at":
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "updated_at":
          aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "status":
          aValue = a.active
            ? t("myUserManagement.activeChip")
            : t("myUserManagement.inactiveChip");
          bValue = b.active
            ? t("myUserManagement.activeChip")
            : t("myUserManagement.inactiveChip");
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Calculate stats - not used in compact header anymore
  // const activeUsers = aggregatedUsers.filter((user) => user.active).length;
  // const userTypes = new Set(aggregatedUsers.map((user) => user.user_type)).size;

  if (myusersloading && pages.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        borderRadius={2}
      >
        <CircularProgress
          size={48}
          aria-label={t("myUserManagement.loadingAria")}
        />
        <Typography variant="body1" ml={2} color="text.secondary">
          {t("myUserManagement.loadingUsers")}
        </Typography>
      </Box>
    );
  }

  if (errorMyUsers) {
    return (
      <Box p={3} borderRadius={2} textAlign="center">
        <Typography variant="h6" color={theme.palette.error.main}>
          {t("myUserManagement.errorTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("myUserManagement.errorMessage")}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => refetchUsers()}
          sx={{ mt: 2 }}
        >
          {t("myUserManagement.retryButton")}
        </Button>
      </Box>
    );
  }

  if (!aggregatedUsers.length) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
        }}
      >
        <PageHeader
          icon="👥"
          title={t("myUserManagement.title")}
          subtitle={t("myUserManagement.subtitle")}
          stats={[{ label: "0" }]}
          actions={[
            {
              label: t("myUserManagement.addUserButton"),
              icon: <FaUserPlus />,
              onClick: () => setOpenAddForm(true),
              ariaLabel: t("myUserManagement.addUserButtonAria"),
            },
          ]}
          t={t}
          translationPrefix="myUserManagement"
          compact={true}
        />
        <EmptyUsersState />
      </Box>
    );
  }

  const sortedUsers = sortUsers(aggregatedUsers);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Compact Enterprise Header */}
      <PageHeader
        icon="👥"
        title={t("myUserManagement.title")}
        subtitle={t("myUserManagement.subtitle")}
        stats={[{ label: `${aggregatedUsers.length}` }]}
        actions={[
          {
            label: t("myUserManagement.addUserButton"),
            icon: <FaUserPlus />,
            onClick: () => setOpenAddForm(true),
            ariaLabel: t("myUserManagement.addUserButtonAria"),
          },
        ]}
        t={t}
        translationPrefix="myUserManagement"
        compact={true}
      />

      {myusersloading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            aria-label={t("myUserManagement.loadingProgressAria")}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TableContainer sx={{ maxHeight: "600px" }}>
          <Table stickyHeader aria-label={t("myUserManagement.tableAria")}>
            <UsersTableHeader sortConfig={sortConfig} onSort={handleSort} />
            <TableBody>
              {sortedUsers.map((user) => (
                <UserTableRow
                  key={user.id}
                  user={user}
                  isExpanded={expandedUserId === user.id}
                  userDetails={userDetails}
                  onToggleExpand={handleAccordion}
                  onEdit={handleOpenEditModal}
                  onDelete={handleDeleteClick}
                  onViewProfile={handleUserDetails}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Enhanced Pagination Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
            px: 2,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="contained"
            onClick={handleNextPage}
            disabled={!hasNextPage || myusersloading}
            aria-label={t("myUserManagement.loadMoreButtonAria")}
            startIcon={
              myusersloading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
              transition: "all 0.2s ease",
            }}
          >
            {myusersloading
              ? t("myUserManagement.loadingText")
              : t("myUserManagement.loadMoreButton")}
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <NewDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        itemType={t("myUserManagement.user")}
      />

      {/* Add/Edit User */}
      {openAddForm && (
        <AddUser
          onUserAdd={refetchUsers}
          onClose={() => setOpenAddForm(false)}
        />
      )}
      {openEditForm && (
        <EditUser
          open={openEditForm}
          onClose={() => setOpenEditForm(false)}
          userDetails={editedUserDetails}
          userId={editingUser}
          setEditingUser={setEditingUser}
        />
      )}

      {viewProfile && (
        <ViewUserProfile
          user={viewedUser}
          open={viewProfile}
          onClose={() => setViewProfile(false)}
        />
      )}

      {/* Toast */}
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
};

export default MyUserManagement;
